import clsx from 'clsx';
import { useMemo, useState } from 'react';
import config from '../config';
import { getTraitDependencies } from '../config/shuffleTraits';
import useTraitShuffleStore from '../hooks/useTraitShuffleStore';
import formatNumber from '../utils/formatNumber';
import { getTraitImagePath } from '../utils/traits';
import Modal from './Modal';
import Tag from './Tag';

const TraitShufflePreviewLayer = ({ layer, allKitties }) => {
  const { selectedNft, selectTrait, selectedTrait, setPreviewTrait } = useTraitShuffleStore();
  const firstKittyLayerValue = selectedNft.metadata?.properties?.[layer];
  const firstKittyImageUrl = useMemo(() => getTraitImagePath(layer, firstKittyLayerValue), [layer, firstKittyLayerValue]);

  // State for variation modal
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Calculate number of kitties with each trait in this layer
  const calculateTraitCounts = useMemo(() => {
    if (!allKitties || allKitties.length === 0) return {};

    const traitCounts = {};

    allKitties.forEach((kitty) => {
      const traitValue = kitty.properties[layer];
      if (traitValue) {
        if (!traitCounts[traitValue]) {
          traitCounts[traitValue] = 0;
        }
        traitCounts[traitValue]++;
      }
    });

    return traitCounts;
  }, [allKitties, layer]);

  const onClick = () => {
    selectTrait(layer);
  };

  if (!config?.shuffleTraits?.[layer] || config?.shuffleTraits?.[layer]?.length === 0) {
    return null;
  }

  // Process traits to group variations
  const processedTraits = useMemo(() => {
    // Get all traits for this layer
    const layerTraits = config?.shuffleTraits?.[layer] || [];

    // Group traits by their base name (before the dash)
    const groupedTraits = {};

    layerTraits.forEach((trait) => {
      // Check if this is a variation (contains a dash)
      if (trait.name.includes(' - ')) {
        const [baseName, variation] = trait.name.split(' - ');

        if (!groupedTraits[baseName]) {
          groupedTraits[baseName] = {
            base: baseName,
            variations: [],
            rarity: trait.rarity,
          };
        }

        groupedTraits[baseName].variations.push({
          fullName: trait.name,
          variation,
          rarity: trait.rarity,
        });
      } else {
        // Not a variation
        if (!groupedTraits[trait.name]) {
          groupedTraits[trait.name] = {
            base: trait.name,
            variations: [],
            rarity: trait.rarity,
          };
        }
      }
    });

    // Convert object to array
    return Object.values(groupedTraits);
  }, [layer]);

  // Handle clicking on a trait with variations
  const openVariationsModal = (trait) => {
    setSelectedVariation(trait);
    setModalOpen(true);
  };

  // Handle clicking on a trait to preview it
  const handleTraitClick = (traitFullName, rarity) => {
    const dependencies = getTraitDependencies(layer, traitFullName);

    setPreviewTrait({
      layer: layer,
      value: traitFullName,
      rarity: rarity,
      dependencies: dependencies,
    });
  };

  return (
    <>
      <div className='flex w-full flex-col gap-2 md:gap-6'>
        <div className='flex w-full flex-col gap-2 h-full'>
          <button
            type='button'
            onClick={onClick}
            className={clsx(
              'flex flex-col md:flex-row md:h-[92px] items-center gap-4 border-2 text-center md:text-left md:gap-8 rounded-lg bg-gray-900 p-2 md:p-4',
              selectedTrait === layer ? 'border-primary-500' : 'border-transparent'
            )}
          >
            <div className='aspect-square max-w-[60px] overflow-hidden rounded-lg bg-gray-800 shrink-0'>
              <img src={firstKittyImageUrl} alt={layer} width={300} height={300} />
            </div>
            <div>
              <p className={clsx('text-xs md:text-sm text-gray-300')}>{layer}</p>
              <p className={clsx('text-sm md:text-xl text-gray-100')}>{firstKittyLayerValue}</p>
            </div>
          </button>
        </div>

        {selectedTrait === layer && (
          <div>
            <h3 className='font-bold text-base text-white mb-4 flex items-center gap-2'>✨ Possible Traits ✨</h3>

            <div className='bg-gray-800 rounded-lg p-4 mb-6 text-sm text-gray-300'>
              <p className='mb-2'>
                <span className='text-yellow-400 font-medium'>Trait chances:</span> Each trait has different rarity levels -
                <span className='text-yellow-300 mx-1'>💎 Ultra Rare</span>,<span className='text-blue-300 mx-1'>🌟 Rare</span>,
                <span className='text-gray-300 mx-1'>Uncommon</span>, and
                <span className='text-gray-400 mx-1'>Common</span>.
              </p>
              <p className='mb-2'>
                <span className='text-green-400 font-medium'>Preview:</span> Click on any trait to preview how it would look on your kitty! Traits
                with a <span className='bg-gray-700 px-1 rounded text-xs'>+</span> badge have multiple variations to explore.
              </p>
              <p className='mb-2'>
                <span className='text-blue-400 font-medium'>Dependencies:</span> Some traits automatically change other parts of your kitty (like
                requiring "None" for Head when wearing certain clothing). These changes are shown in the preview.
              </p>
              <p>
                <span className='text-red-400 font-medium'>Burned:</span> All traits that are replaced are burned and cannot be recovered.
              </p>
            </div>
            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
              {processedTraits.map((trait) => {
                const displayName = trait.base;
                const hasVariations = trait.variations.length > 0;
                const traitFullName = hasVariations ? trait.variations[0].fullName : trait.base;
                const variationText = hasVariations ? `+${trait.variations.length}` : '';
                const isUltraRare = trait.rarity === 'Ultra Rare';
                const isRare = trait.rarity === 'Rare';
                const isUncommon = trait.rarity === 'Uncommon';
                const dependencies = getTraitDependencies(layer, traitFullName);
                const hasDependencies = Object.keys(dependencies).length > 0;

                // Calculate total count for traits with variations
                // For traits with variations, we need to count kitties with the base trait name
                // since the actual kitty metadata uses the base name (e.g., "Track Suit")
                // not the specific variants (e.g., "Track Suit - 230")
                const traitCount = hasVariations ? calculateTraitCounts[trait.base] || 0 : calculateTraitCounts[traitFullName] || 0;

                return (
                  <div
                    key={trait.base}
                    className='relative bg-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-700 transition-colors'
                    onClick={() => (hasVariations ? openVariationsModal(trait) : handleTraitClick(traitFullName, trait.rarity))}
                  >
                    <div className='aspect-square overflow-hidden rounded-lg bg-gray-900 mb-2 relative'>
                      <img
                        src={getTraitImagePath(layer, traitFullName)}
                        alt={displayName}
                        className='w-full h-full object-cover'
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {hasVariations && (
                        <div className='absolute top-1 right-1 bg-primary-500 text-white text-xs px-1 py-0.5 rounded'>{variationText}</div>
                      )}
                      {hasDependencies && (
                        <div className='absolute top-1 left-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded' title='Has dependencies'>
                          🔗
                        </div>
                      )}
                    </div>

                    <div className='flex flex-col gap-1 mt-2 relative z-20'>
                      <div className='font-medium text-sm truncate flex'>
                        <span>{displayName}</span>
                        {variationText && <span className='ml-1 text-gray-400'>{variationText}</span>}
                      </div>
                      <div className='flex items-start gap-1 flex-col'>
                        <Tag theme={isUltraRare ? 'ultra-rare' : isRare ? 'rare' : isUncommon ? 'uncommon' : 'gray'} title='Rarity category'>
                          {isUltraRare && '💎 '}
                          {isRare && '🌟 '}
                          {trait.rarity}
                        </Tag>
                        <Tag
                          theme='primary'
                          title={
                            hasVariations
                              ? 'Total number of existing kitties with any variation of this trait'
                              : 'Number of existing kitties with this trait'
                          }
                        >
                          {formatNumber(traitCount)} / {formatNumber(allKitties.length)}
                        </Tag>
                        {hasDependencies && (
                          <div className='flex flex-wrap gap-1 mt-1'>
                            {Object.entries(dependencies).map(([depLayer, depValue]) => (
                              <Tag key={depLayer} theme='blue' title={`Requires ${depLayer} = ${depValue}`}>
                                🔗 {depLayer}: {depValue}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Variations Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        {selectedVariation && (
          <div className='p-4'>
            <h3 className='text-lg font-bold text-white mb-4'>{selectedVariation.base} Variations</h3>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
              {selectedVariation.variations.map((variation) => {
                const isUltraRare = selectedVariation.rarity === 'Ultra Rare';
                const isRare = selectedVariation.rarity === 'Rare';
                const isUncommon = selectedVariation.rarity === 'Uncommon';
                const dependencies = getTraitDependencies(layer, variation.fullName);
                const hasDependencies = Object.keys(dependencies).length > 0;

                return (
                  <div
                    key={variation.fullName}
                    className='bg-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-700 transition-colors'
                    onClick={() => {
                      handleTraitClick(variation.fullName, variation.rarity);
                      setModalOpen(false);
                    }}
                  >
                    <div className='aspect-square overflow-hidden rounded-lg bg-gray-900 mb-2 relative'>
                      <img
                        src={getTraitImagePath(layer, variation.fullName)}
                        alt={variation.variation}
                        className='w-full h-full object-cover'
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {hasDependencies && (
                        <div className='absolute top-1 left-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded' title='Has dependencies'>
                          🔗
                        </div>
                      )}
                    </div>

                    <div className='mt-2 text-white'>
                      <p className='font-medium'>{variation.variation}</p>
                      <div className='mt-1'>
                        <Tag theme={isUltraRare ? 'ultra-rare' : isRare ? 'rare' : isUncommon ? 'uncommon' : 'gray'} title='Rarity category'>
                          {isUltraRare && '💎 '}
                          {isRare && '🌟 '}
                          {selectedVariation.rarity}
                        </Tag>
                        {hasDependencies && (
                          <div className='flex flex-wrap gap-1 mt-1'>
                            {Object.entries(dependencies).map(([depLayer, depValue]) => (
                              <Tag key={depLayer} theme='blue' title={`Requires ${depLayer} = ${depValue}`}>
                                🔗 {depLayer}: {depValue}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default TraitShufflePreviewLayer;
